import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, MapPin, Home, FileText, Hammer, CheckCircle, Shield, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { constructionProjects } from '../mockData';

const ProjetConstructionPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Notre accompagnement</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Notre accompagnement de projet de construction</h1>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Accompagnement de projet de construction avec MRTP SARL</h2>
          <p className="text-lg text-gray-600 mb-4">
            Faire construire sa maison est une aventure passionnante, mais elle peut rapidement devenir complexe sans un cadre bien défini. Entre la sélection du terrain, la conception des plans et le suivi du chantier, chaque étape demande une attention particulière. Bénéficier de notre <strong>accompagnement projet de construction de maison</strong> permet d'éviter les erreurs et d'assurer le bon déroulement des travaux.
          </p>
          <p className="text-lg text-gray-600">
            Chez <Link to="/a-propos" className="text-[#c4d600] hover:underline">MRTP SARL</Link>, nous proposons un accompagnement projet de construction de maison complet, basé sur l'écoute, la transparence et l'expertise. Notre mission est de simplifier votre projet, sécuriser chaque phase et vous fournir toutes les informations nécessaires. Ainsi, vous pourrez donc <strong>construire votre maison en toute confiance</strong>.
          </p>
        </div>
      </div>

      {/* Section 1: Écoute attentive */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c4d600] font-semibold mb-2">UNE ECOUTE ATTENTIVE</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">DÈS LE DEBUT DE VOTRE PROJET</h2>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Chaque projet de construction est unique</h3>
              <p className="text-gray-600 mb-4">
                C'est pourquoi nous commençons toujours par une étude approfondie de vos besoins et de vos attentes. Lors de notre premier rendez-vous, nous prenons le temps d'échanger sur vos envies, votre mode de vie, votre budget et vos exigences en matière de confort et d'esthétique.
              </p>
              <p className="text-gray-600 mb-4">
                <strong>Nous vous conseillons</strong> sur les différentes possibilités qui s'offrent à vous : choix du type de maison (plain-pied, étage, maison traditionnelle ou contemporaine), optimisation des espaces, orientation de la maison pour maximiser l'ensoleillement…
              </p>
              <p className="text-gray-600">
                Cette première phase est en effet déterminante pour concevoir une maison qui correspond à vos attentes tout en respectant les contraintes techniques et budgétaires.
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Consultation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Choix du terrain */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800"
                alt="Terrain"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#c4d600] font-semibold mb-2">AIDE AU CHOIX ET</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">À L'ACHAT DU TERRAIN</h2>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Le <Link to="/nos-terrains" className="text-[#c4d600] hover:underline">choix du terrain</Link> est une étape déterminante dans votre projet de construction
              </h3>
              <p className="text-gray-600 mb-6">
                Son emplacement, ses caractéristiques et sa viabilisation influencent directement la conception et la faisabilité de votre future maison. Pour vous aider à faire le bon choix, notre équipe vous accompagne dans l'analyse des critères essentiels.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-[#c4d600] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Localisation et environnement :</h4>
                    <p className="text-gray-600 text-sm">Proximité des commodités, cadre de vie, exposition…</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Hammer className="w-6 h-6 text-[#c4d600] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Caractéristiques du sol :</h4>
                    <p className="text-gray-600 text-sm">Nature du sol, risques de remblais ou de nappes phréatiques…</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FileText className="w-6 h-6 text-[#c4d600] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Réglementation locale :</h4>
                    <p className="text-gray-600 text-sm">Règles d'urbanisme et de constructibilité (PLU, règlement du lotissement…).</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#c4d600] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Viabilisation et raccordements aux réseaux :</h4>
                    <p className="text-gray-600 text-sm">Nous vérifions si le terrain est déjà équipé ou réalisons les démarches nécessaires.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Conception */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c4d600] font-semibold mb-2">CONCEPTION ET PERSONNALISATION DE</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">VOTRE MAISON</h2>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Un accompagnement sur mesure pour vos plans</h3>
              <p className="text-gray-600 mb-4">
                Une fois le terrain choisi, il est temps de <strong>concevoir les plans</strong> de votre future maison. Chez MRTP SARL, nous vous accompagnons dans cette étape essentielle afin de créer <strong>un espace de vie qui vous ressemble</strong>. L'objectif est d'optimiser chaque mètre carré en assurant donc une parfaite harmonie entre fonctionnalité, esthétique et confort.
              </p>
              <p className="text-gray-600 mb-4">
                Nos dessinateurs conçoivent donc les plans en tenant compte de votre mode de vie, de vos préférences architecturales et des contraintes techniques du terrain. <strong>L'implantation de la maison</strong> est alors soigneusement étudiée pour maximiser l'ensoleillement et améliorer l'efficacité énergétique.
              </p>
              <p className="text-gray-600">
                Afin de garantir une <strong>construction durable et performante</strong>, nous vous conseillons également sur le choix des matériaux, des solutions énergétiques et des équipements les plus adaptés à vos besoins.
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800"
                alt="Plans de maison"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Démarches administratives */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">GESTION DES DEMARCHES ADMINISTRATIVES</h2>
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Notre suivi des démarches</h3>
          <p className="text-lg text-gray-600 mb-8">
            La construction d'une maison implique en effet plusieurs <strong>démarches administratives</strong> qu'il ne faut pas négliger. La MRTP SARL prend en effet, en charge toutes ces formalités pour vous simplifier la tâche :
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <FileText className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Permis de construire</h4>
              <p className="text-sm text-gray-600">Dépôt et suivi de son instruction</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <CheckCircle className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Normes</h4>
              <p className="text-sm text-gray-600">Respect des normes RE2020 et accessibilité PMR</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <Shield className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Garanties</h4>
              <p className="text-sm text-gray-600">Garantie dommages ouvrage et garantie de livraison</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Suivi du chantier */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">UN SUIVI RIGOUREUX</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">DU CHANTIER JUSQU'A LA LIVRAISON</h2>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Projet de construction de maison : MRTP SARL avec vous de A à Z</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <p className="text-gray-600 mb-6">
                Lorsque la construction démarre, nous assurons en effet un <strong>suivi minutieux de chaque étape</strong> pour garantir le bon déroulement des travaux. Grâce à notre expertise et à notre réseau d' <strong>artisans qualifiés</strong>, nous veillons donc à ce que votre maison soit construite dans les meilleures conditions.
              </p>
              
              <h4 className="font-bold text-gray-800 mb-3">Visites à différentes étapes clés :</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#c4d600] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Pose des fondations et du soubassement</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#c4d600] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Élévation des murs et mise hors d'eau/hors d'air</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#c4d600] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Installation des équipements</span>
                </li>
              </ul>
            </div>
            
            <div className="relative h-80 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800"
                alt="Chantier"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 6: Après livraison */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">UN ACCOMPAGNEMENT</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-6">APRES LA REMISE DES CLES</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Notre engagement ne s'arrête pas à la livraison de votre maison. <strong>Nous restons donc à votre disposition</strong> pour toute question ou intervention nécessaire après votre emménagement. Nos garanties vous assurent alors une tranquillité d'esprit.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Shield className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Garantie de parfait achèvement</h4>
              <p className="text-sm text-gray-600">Permet la correction des éventuels défauts signalés après la réception</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Shield className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Garantie biennale</h4>
              <p className="text-sm text-gray-600">Protège les équipements comme la robinetterie ou le chauffe-eau pendant deux ans</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Shield className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Garantie décennale</h4>
              <p className="text-sm text-gray-600">Couvre les éventuels défauts de construction pouvant affecter la solidité de la maison</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <Users className="w-12 h-12 text-[#c4d600] mx-auto mb-4" />
              <h4 className="font-bold text-gray-800 mb-2">Service après construction</h4>
              <p className="text-sm text-gray-600">Notre SAV reste disponible pour répondre à vos questions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-[#c4d600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">POURQUOI CHOISIR LA MAISON DES COMPAGNONS POUR VOTRE PROJET ?</h2>
          <p className="text-lg text-gray-800 mb-8">
            Faire construire sa maison est une étape majeure dans une vie. Pour la réussir, il est donc essentiel d'être bien entouré. Avec La MRTP SARL, vous bénéficiez donc d'un <strong>accompagnement sur-mesure</strong> et d'un interlocuteur unique. Nous adoptons également une <strong>transparence totale</strong> à chaque phase du projet.
          </p>
          
          <div className="bg-white rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Notre engagement :</h3>
            <div className="space-y-3 text-left max-w-2xl mx-auto">
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Une écoute attentive et des conseils personnalisés</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Une expertise reconnue dans la construction de maisons individuelles</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Un suivi rigoureux et un respect des délais</span>
              </div>
            </div>
          </div>
          
          <p className="text-lg text-gray-800 mb-6">
            Construisez donc votre maison en toute sérénité avec un partenaire de confiance vous offrant un accompagnement projet de construction de maison !
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-gray-800 hover:bg-gray-900 text-white font-semibold">
              <Link to="/maisons-sur-mesure">Découvrez nos maisons sur mesure</Link>
            </Button>
            <Button asChild className="bg-white hover:bg-gray-100 text-gray-800 font-semibold">
              <Link to="/modeles">Nos modèles de maisons</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Construction Projects Section */}
      {constructionProjects && Array.isArray(constructionProjects) && constructionProjects.length > 0 && (
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#c4d600] font-semibold mb-2">NOS RÉALISATIONS</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Projets de construction en cours</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Découvrez nos projets de construction actuellement en cours dans les Alpes-de-Haute-Provence
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {constructionProjects.map((project) => (
                project && (
                <Link
                  key={project.id}
                  to={`/projet/${project.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 block"
                >
                  <div className="relative h-64 overflow-hidden bg-gray-200">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                        e.target.onerror = null;
                      }}
                    />
                    <div className="absolute top-4 right-4 bg-[#c4d600] text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                      {project.status}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-[#c4d600] transition-colors">
                      {project.title}
                    </h3>
                    {project.subtitle && (
                      <p className="text-sm text-gray-600 mb-3">{project.subtitle}</p>
                    )}
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-2 text-[#c4d600]" />
                      <span>{project.commune || project.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-[#c4d600] font-semibold">
                      <Building2 className="w-4 h-4 mr-2" />
                      <span>{project.projectType}</span>
                    </div>
                  </div>
                </Link>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA to next page */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Découvrez les étapes de construction de maison</h3>
          <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg px-8 py-6">
            <Link to="/etapes-construction">Je découvre les étapes</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjetConstructionPage;